import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Container, Box, Paper, CircularProgress, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png'; // Allam School logo
import loginIllustration from '../assets/login-illustration.png'; // Example illustration
import { useTranslation } from 'react-i18next';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth(); // Get login function and user state
  const navigate = useNavigate();
  const { t } = useTranslation(); // Hook to access translations

  // Redirect based on user role after login
  useEffect(() => {
    if (user) {
      if (user.role_id === 3) {
        navigate('/student/dashboard');
      } else if (user.role_id === 2) {
        navigate('/teacher/dashboard');
      } else if (user.role_id === 1) {
        navigate('/admin/dashboard');
      } else {
        navigate('/'); // Default redirect for other roles or no role
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password); // Call the login function
    } catch (error) {
      setError('Login failed. Please check your credentials and try again.');
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1A73E8, #6A1B9A)', // Gradient background
        backgroundSize: 'cover',
      }}
    >
      <Paper
        elevation={3}
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          padding: 4,
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center',
          borderRadius: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)', // Semi-transparent white
          backdropFilter: 'blur(10px)', // Blur effect
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src={logo}
          alt="Allam School Logo"
          sx={{
            width: '100px',
            height: '100px',
            borderRadius: '50%', // Circular logo
            mb: 2,
          }}
        />

        <Typography variant="h4" gutterBottom sx={{ color: '#1A73E8', fontWeight: 'bold' }}>
          Allam School
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Empowering Future Leaders
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                mt: 2,
                mb: 2,
                backgroundColor: '#1A73E8',
                '&:hover': { backgroundColor: '#1557B0' },
                borderRadius: '8px',
                padding: '10px',
                fontSize: '16px',
                fontWeight: 'bold',
                textTransform: 'none', // Remove uppercase
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('login')}
            </Button>
          </motion.div>
        </form>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            <a href="/forgot-password" style={{ textDecoration: 'none', color: '#1A73E8' }}>
              Forgot Password?
            </a>
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Don't have an account?{' '}
            <a href="/signup" style={{ textDecoration: 'none', color: '#1A73E8' }}>
              Sign Up
            </a>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;